import { Injectable } from '@angular/core';
import { ApiHttp, SCHEMA_VERSION } from '@core/api/api-http';
import { ContactEventService } from './contact-event.service';
import { Observable, map, switchMap, take } from 'rxjs';
import { EntityType } from '../models';
import { DiaryNoteService } from '@features/diary-notes/services/diary-note.service';
import { DiaryNote } from '@features/diary-notes/model/diary-note';
import { flattenCollection } from '@features/opportunities/services/contract';
import { environment } from '@env/environment';

@Injectable()
export class ContactDiaryNoteService extends DiaryNoteService {
  constructor(
    private http: ApiHttp,
    private contactEventService: ContactEventService
  ) {
    super();
  }

  entityId$ = this.contactEventService.selectedContactInfo$.pipe(
    map((c) => c.id)
  );

  private entityTypePath$ = this.contactEventService.selectedContactInfo$.pipe(
    map((c) => this.getEntityPath(c.entityType))
  );

  getDiaryNotes(
    entityId: string,
    searchString: string
  ): Observable<DiaryNote.Model[]> {
    let params: any = {};
    if (searchString) {
      params.Search = searchString;
    }

    let queryParams = Object.keys(params) ? params : null;

    let httpOptions = {
      headers: ApiHttp.getStandardOptions().headers,
      queryParams
    };
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http
          .getEntity(
            environment.clientsApiUrl,
            `${p}/${entityId}/diaryNotes`,
            entitySchemaVersion,
            httpOptions
          )
          .pipe(
            map((x) =>
              flattenCollection<DiaryNote.SchemaContract>(x).map((x) =>
                DiaryNote.toModel(x)
              )
            )
          )
      )
    );
  }

  getDiaryNoteDefinition(): Observable<DiaryNote.Definition> {
    return this.entityTypePath$.pipe(
      take(1),
      switchMap(([p, entitySchemaVersion]) =>
        this.http.getEntity<DiaryNote.ClientSchemaDefinition>(
          environment.clientsApiUrl,
          `${p}/schema/diaryNotes`,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      ),
      map(DiaryNote.mapClientSchemaToDefinition)
    );
  }

  getDiaryNoteForEdit(
    entityId: string,
    diaryNoteId: string
  ): Observable<DiaryNote.Model> {
    return this.entityTypePath$.pipe(
      take(1),
      switchMap(([p, entitySchemaVersion]) =>
        this.http.getEntity<DiaryNote.Model>(
          environment.clientsApiUrl,
          `${p}/${entityId}/diaryNotes/${diaryNoteId}`,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      ),
      map(DiaryNote.toModel)
    );
  }

  updateDiaryNote(
    entityId: string,
    diaryNoteId: string,
    change: any
  ): Observable<any> {
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http.put(
          environment.clientsApiUrl,
          `${p}/${entityId}/diaryNotes/${diaryNoteId}`,
          change,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      )
    );
  }

  deleteDiaryNote(entityId: string, diaryNoteId: string): Observable<any> {
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http.deleteEntity(
          environment.clientsApiUrl,
          `${p}/${entityId}/diaryNotes/${diaryNoteId}`,
          entitySchemaVersion
        )
      )
    );
  }

  private getEntityPath(entityType: EntityType): [string, number] {
    switch (entityType) {
      case EntityType.Company:
        return ['companies', SCHEMA_VERSION.COMPANY];
      case EntityType.Individual:
        return ['individuals', SCHEMA_VERSION.INDIVIDUAL];
      case EntityType.Trust:
        return ['trusts', SCHEMA_VERSION.TRUST];
    }
  }
}
